import { Alert, Checkbox, InputNumber, Modal } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { buildLimitPayload, exceedsTeamAvailable, MAX_MEMBER_LIMIT } from './teamCreditsUsage'
import { setMemberCreditLimit, teamErrorMessage } from './teamApi'
import type { MemberAllowance } from './teamTypes'
import { useTeamFormat } from './teamUi'

/**
 * VistoeraTASK-00529 - edit one member's Monthly Spending Limit. A limit is a
 * consumption constraint on the shared Workspace credit pool (not a wallet, not
 * a transfer). Validation here is convenience only; the backend re-validates.
 */
export default function CreditLimitModal({
  workspaceId,
  member,
  teamAvailable,
  onClose,
  onSaved,
}: {
  workspaceId: string
  member: MemberAllowance | null
  /** TEAM_AVAILABLE_CREDITS from the member list; `null`/undefined = unknown (backend still validates). */
  teamAvailable?: string | null
  onClose: () => void
  onSaved: (row: MemberAllowance) => void
}) {
  const { t } = useTranslation('team')
  const { num } = useTeamFormat()
  const [unlimited, setUnlimited] = useState(true)
  const [value, setValue] = useState<number | null>(null)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!member) return
    setUnlimited(member.unlimited)
    setValue(member.monthly_limit === null ? null : Number(member.monthly_limit))
    setFieldError(null)
    setError(null)
  }, [member])

  const submit = async () => {
    if (!member) return
    const built = buildLimitPayload(unlimited ? { unlimited: true } : { unlimited: false, value }, teamAvailable)
    if (!built.ok) {
      setFieldError(t(`credits.limit.err.${built.error}`))
      return
    }
    setFieldError(null)
    setSaving(true)
    setError(null)
    try {
      const res = await setMemberCreditLimit(workspaceId, member.user_id, built.payload)
      onSaved(res.data)
    } catch (e) {
      setError(teamErrorMessage(e, t('credits.limit.failed')))
    } finally {
      setSaving(false)
    }
  }

  const overTeam = !unlimited && exceedsTeamAvailable(value, teamAvailable)

  return (
    <Modal
      open={member !== null}
      title={t('credits.limit.title', { name: member?.display_name ?? '' })}
      okText={t('credits.limit.save')}
      cancelText={t('common.cancel')}
      onCancel={onClose}
      onOk={() => void submit()}
      confirmLoading={saving}
      okButtonProps={{ disabled: overTeam }}
      destroyOnHidden
      data-testid="team-limit-modal"
    >
      <p className="tm-hint" style={{ marginTop: 0 }}>
        {t('credits.limit.intro')}
      </p>
      <div className="tm-field-label">{t('credits.limit.field')}</div>
      <InputNumber<number>
        style={{ width: '100%' }}
        min={0}
        max={MAX_MEMBER_LIMIT}
        disabled={unlimited}
        value={value}
        onChange={(v) => setValue(v)}
        placeholder={t('credits.limit.placeholder')}
        suffix={t('credits.unit')}
        status={fieldError || overTeam ? 'error' : undefined}
        data-testid="team-limit-input"
      />
      <Checkbox
        checked={unlimited}
        onChange={(e) => {
          setUnlimited(e.target.checked)
          setFieldError(null)
        }}
        style={{ marginTop: 10 }}
        data-testid="team-limit-unlimited"
      >
        {t('credits.limit.unlimited')}
      </Checkbox>
      {overTeam && !fieldError && (
        <div className="tm-field-error" data-testid="team-limit-exceeds-team">
          {t('credits.limit.err.exceedsTeam')}
        </div>
      )}
      {fieldError && <div className="tm-field-error">{fieldError}</div>}
      {teamAvailable != null && (
        <div className="tm-hint" style={{ marginTop: 8 }} data-testid="team-limit-team-available">
          {t('credits.limit.teamAvailable', { available: num(teamAvailable) })}
        </div>
      )}
      {member && (
        <div className="tm-hint" style={{ marginTop: 12 }}>
          {t('credits.limit.currentUsed', { used: num(member.month_used) })}
        </div>
      )}
      {error && <Alert type="error" showIcon title={error} style={{ marginTop: 12 }} />}
    </Modal>
  )
}
